/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/**
 * The one encoder for query strings this app puts in the address bar.
 *
 * `URLSearchParams.toString()` is form-urlencoded, so it writes a space as `+`, while
 * `encodeURIComponent` writes `%20`. Both used to be in play here, so the same vehicle could
 * appear as `model=SILVERADO+1500` from one screen and `model=SILVERADO%201500` from the next.
 * Nothing broke -- `URLSearchParams` reads either back as a space -- but the two spellings are
 * different strings, and these links are copied into tickets, bookmarked, logged against a user's
 * session and handed to Service Information and TLC, whose parsers are not ours to choose. `%20`
 * is a space under both percent-encoding and form rules; `+` only under the latter, so a reader
 * calling `decodeURIComponent` gets a literal plus. We emit `%20` and accept both on the way in.
 *
 * `:`, `,`, `;` and `/` are deliberately left literal. All four are legal in a query, and encoding
 * them turns readable deep links (`codes=X604/X618,X700`) into noise for no gain.
 */
function encodeComponent(value: string): string {
  return encodeURIComponent(value)
    .replace(/%3A/gi, ':')
    .replace(/%2C/gi, ',')
    .replace(/%3B/gi, ';')
    .replace(/%2F/gi, '/');
}

/** Serialize params to a query string WITHOUT a leading `?`; empty when there is nothing to carry. */
export function encodeSearch(params: URLSearchParams): string {
  const parts: string[] = [];
  for (const [key, value] of params.entries()) {
    parts.push(`${encodeComponent(key)}=${encodeComponent(value)}`);
  }
  return parts.join('&');
}

/** Serialize params to `?a=b`, or `''` when empty, ready to append to a path. */
export function searchString(params: URLSearchParams): string {
  const query = encodeSearch(params);
  return query ? `?${query}` : '';
}
